<template>
  <div
    ref="dropdownTrigger"
    data-cy="dropdownWrapper"
    class="relative flex flex-1 flex-col select-none"
    :class="[{ open: showMenu }, getWrapperBackgroundClass]"
    :name="name"
  >
    <slot
      name="toggle-button"
      :toggle="toggleMenu"
      :value="inputValue"
      :menu-open="showMenu"
    >
      <lf-badge
        v-if="asBadge"
        class="badge border-2 text-xs font-medium transition-all duration-300"
        :class="[
          getBadgeColorClass,
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
          { 'max-w-[-webkit-fill-available]': !!pickedValue },
        ]"
        is-rounded
        data-cy="dropdown-badge"
        @click.stop="toggleMenu"
      >
        <div class="flex space-x-2 overflow-hidden py-0-25">
          <div v-if="!inputValue && showWarningIcon">
            <icon-base
              :name="`${name}-warning-icon`"
              class="translate-y-0-25 transform transition-all duration-300"
              :class="warningAsError ? 'text-error' : 'text-warning'"
              icon="warning"
              height="16"
              width="16"
            />
          </div>
          <span v-if="showPlaceholder">
            {{ placeholder }}
          </span>
          <span
            v-else-if="pickedValue"
            v-tooltipable
            class="badge-value overflow-hidden text-ellipsis whitespace-nowrap"
          >
            {{ pickedValue }}
          </span>
          <span v-else>
            {{ placeholder }}
          </span>
          <span class="flex items-center text-gray-400" v-if="!disabled">
            <icon-base
              class="transform transition-all"
              height="6"
              width="10"
              icon="dropdown-arrow"
              :class="{ 'rotate-180': showMenu }"
            />
          </span>
        </div>
      </lf-badge>
      <template v-else>
        <span
          v-show="shouldShowLabel"
          class="absolute z-1 -mt-3 ml-2-5 w-min px-1-25 text-xs whitespace-nowrap"
          :class="[
            disabled ? 'bg-gray-50' : 'bg-white',
            showMenu ? 'text-blue-500' : 'text-gray-400',
          ]"
        >
          {{ placeholder }}
        </span>
        <button
          v-if="!filterIcon"
          type="button"
          aria-haspopup="listbox"
          aria-expanded="true"
          aria-labelledby="listbox-label"
          class="dropdown-button relative flex w-full cursor-pointer items-center rounded-sm py-1 pr-8 pl-4 text-left text-sm focus:outline-hidden"
          :class="getButtonClasses"
          :disabled="disabled"
          :role="`${name}-dropdown`"
          :value="inputValue ? `${inputValue}` : undefined"
          @click.stop="toggleMenu"
        >
          <span v-if="!!icons && inputValue && optionIcon(inputValue).icon">
            <icon-base
              :icon="optionIcon(inputValue).icon"
              :view-box="optionIcon(inputValue).viewBox"
              :class="optionIcon(inputValue).class"
              class="mr-3"
            />
          </span>
          <img
            v-if="!!icons && inputValue && !optionIcon(inputValue).icon"
            :src="optionIcon(inputValue)"
            style="width: 20px; height: 20px"
            class="mr-3 inline-block"
            :alt="$t('COMMON.OPTION_ICON', { option: inputValue })"
            loading="lazy"
          />
          <span
            class="input-value block overflow-hidden text-ellipsis whitespace-nowrap"
          >
            {{ pickedValue || placeholder }}
          </span>
          <slot name="type" />
          <span
            class="dropdown-icon absolute inset-y-0 right-0 ml-3 flex items-center pr-2 text-gray-400"
          >
            <icon-base
              v-if="!disabled"
              height="6"
              width="10"
              icon="dropdown-arrow"
              :class="{ 'rotate-180 transform': showMenu }"
            />
          </span>
        </button>
      </template>
    </slot>
    <field-error-message :value="errorMessage" />
    <teleport to="#menus" :disabled="!teleportOptions?.enabled">
      <div
        v-show="toggleFromParent || showMenu"
        ref="dropdown"
        class="dropdown absolute overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-hidden"
        :class="[getDropdownClasses, menuWidth]"
        :style="dropdownStyle"
      >
        <div v-if="searchEnabled" class="w-full min-w-[265px] px-2 py-2">
          <search-input
            :model-value="search"
            :search-term="searchPlaceholder || placeholder"
            no-padding
            no-margin
            @update:model-value="handleSearchChange"
          />
        </div>
        <hr v-if="$slots.search" />
        <div class="relative" :class="{ 'w-full': fullWidth }">
          <loader :is-loading="isLoading" />
          <ul
            id="exp_elem_list"
            :data-cy="`dropdown-${name}`"
            tabindex="-1"
            role="listbox"
            aria-labelledby="listbox-label"
            aria-activedescendant="listbox-item-3"
            class="dropdown-list z-50 max-h-44 w-full overflow-y-auto overscroll-none text-sm text-gray-500"
          >
            <template v-for="(option, key, index) in sortedOptions" :key="key">
              <slot
                :option="option"
                :option-key="key"
                :index="index"
                name="listItem"
              >
                <li
                  :data-cy="option"
                  class="dropdown-option flex w-full justify-start overflow-auto py-2 pr-2 hover:bg-gray-200"
                  role="option"
                  :aria-selected="isSelected(key)"
                  :class="{ 'bg-gray-100': isSelected(key) }"
                  @click="selectOption(key)"
                >
                  <slot
                    name="label"
                    :is-selected="isSelected(key)"
                    :option="option"
                    :option-id="key"
                  >
                    <template v-if="!!icons">
                      <span v-if="optionIcon(key).icon" class="icon">
                        <icon-base
                          :icon="optionIcon(key).icon"
                          :view-box="optionIcon(key).viewBox"
                          :class="optionIcon(key).class"
                        />
                      </span>
                      <img
                        v-else
                        :src="optionIcon(key)"
                        style="width: 20px; height: 20px"
                        class="ml-3"
                        :alt="$t('COMMON.OPTION_ICON', { option: inputValue })"
                        loading="lazy"
                      />
                    </template>
                    <span
                      class="ml-3 font-normal"
                      :class="getOptionClasses(option)"
                    >
                      {{ optionFormatter ? optionFormatter(option) : option }}
                    </span>
                  </slot>
                </li>
              </slot>
            </template>

            <template v-if="isEmpty(sortedOptions)">
              <p class="p-2 text-center">
                {{ customNoDataMessage || $t("COMMON.NO_DATA_AVAILABLE") }}
              </p>
            </template>
            <div
              v-show="showClearFilter || $slots.action"
              class="sticky bottom-0 flex h-11 items-center justify-between border-t bg-white p-0 px-2"
            >
              <a
                v-if="showClearFilter"
                class="transform cursor-pointer text-sm font-medium text-primary active:scale-95"
                @click.stop="clearFilter"
              >
                {{ clearText || $t("COMMON.CLEAR_FILTER") }}
              </a>
              <slot name="action" :search-string="search" />
            </div>
          </ul>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside, useElementVisibility } from "@vueuse/core";
import {
  type PropType,
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  readonly,
  ref,
  watch,
} from "vue";

import isEmpty from "lodash/isEmpty";
import { useField } from "vee-validate";

import { getDropdownWidth } from "@/helpers/UI";
import { sortProperties } from "@/helpers/formatting";
import { useTeleport } from "@/hooks/elements";
import { useOverflowDetection } from "@/hooks/useOverflowDetection";

import LfBadge from "@/components/ui/LfBadge.vue";
import FieldErrorMessage from "@/components/ui/inputs/FieldErrorMessage.vue";
import SearchInput from "@/components/ui/inputs/SearchInput.vue";

interface DropdownButtonClassnames {
  widthClassname: string;
  heightClassname: string;
  headlineTextClassname: string;
}

export interface DropdownTeleportOptions {
  enabled: boolean;
  scrollable: string;
  horizontalScrollable: string;
}

const defaultTeleportOptions: DropdownTeleportOptions = {
  enabled: false,
  horizontalScrollable: "",
  scrollable: "dealsWrapper",
};

/* eslint-disable @typescript-eslint/no-explicit-any */
const slots = defineSlots<{
  "toggle-button"?: (props: {
    toggle: () => void;
    value: string | number | boolean;
    menuOpen: boolean;
  }) => any;
  label: (props: { isSelected: boolean; option: any; optionId: any }) => any;
  action: (props: { searchString: string }) => any;
  type?: (props: Record<string, never>) => any;
  listItem: (props: {
    option: unknown;
    index: number;
    optionKey: string;
  }) => any;
  search?: (props: Record<string, never>) => any;
}>();
/* eslint-enable @typescript-eslint/no-explicit-any */

const emit = defineEmits<{
  "menu:toggle": [value: boolean];
  "search:change": [value: string];
  toggle: [value: boolean];
  change: [option: string | number];
}>();

const props = defineProps({
  options: {
    type: Object,
    default: () => ({}),
  },
  icons: {
    type: Object,
    default: null,
  },
  name: {
    type: String,
    default: "",
  },
  value: {
    type: [String, Number, Boolean],
    default: "",
  },
  placeholder: {
    type: String,
    default: "",
  },
  sort: {
    type: Boolean,
    default: false,
  },
  hidePlaceholderWhenValue: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  dropUp: {
    type: Boolean,
    default: false,
  },
  titleDropdown: {
    type: Boolean,
    default: false,
  },
  showClearFilter: {
    type: Boolean,
    default: false,
  },
  clearText: {
    type: String,
    default: "",
  },
  active: {
    type: Boolean,
    default: false,
  },
  showLabel: {
    type: Boolean,
    default: true,
  },
  capitalize: {
    type: Boolean,
    default: true,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  filterIcon: {
    type: Boolean,
    default: false,
  },
  toggleFromParent: {
    type: Boolean,
    default: null,
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
  customNoDataMessage: {
    type: String,
    default: "",
  },
  asBadge: {
    type: Boolean,
    default: false,
  },
  customButtonClassnames: {
    type: Object as PropType<Partial<DropdownButtonClassnames>>,
  },
  teleportOptions: {
    type: Object as PropType<Partial<DropdownTeleportOptions>>,
  },
  searchEnabled: {
    type: Boolean,
    default: false,
  },
  searchPlaceholder: {
    type: String,
    default: "",
  },
  warningAsError: {
    type: Boolean,
    default: false,
  },
  showWarningIcon: {
    type: Boolean,
    default: true,
  },
  showPlaceholder: {
    type: Boolean,
    default: false,
  },
  optionFormatter: {
    type: Function,
  },
  disabledOptions: {
    type: Array as PropType<Array<string> | Array<number>>,
    default: () => [],
  },
  menuWidth: {
    type: String,
  },
  customZIndex: {
    type: String,
  },
  isStateless: {
    type: Boolean,
    default: false,
  },
  dropdownOffsetTop: {
    type: Number,
    default: 10,
  },
});

// Computed properties for conditional classes
const getWrapperBackgroundClass = computed(() => {
  if (props.disabled && !slots["toggle-button"] && !props.asBadge) {
    return "bg-gray-50";
  }

  return props.asBadge ? "bg-transparent" : "bg-white";
});

const getBadgeColorClass = computed(() => {
  return !inputValue.value && props.warningAsError
    ? "bg-red-100 border-red-200"
    : "border-transparent";
});

const getButtonClasses = computed(() => {
  const classes = [
    dropdownButtonWidth.value,
    dropdownButtonHeight.value,
    headlineTextClassname.value,
    { "border-red-600": !!errorMessage.value },
    { "bg-gray-50": props.disabled },
    { "border-none text-xl font-semibold": props.titleDropdown },
    { capitalize: props.capitalize },
  ];

  // Add border classes based on menu state
  if (showMenu.value) {
    classes.push("border-primary hover:border-primary border-2");
  } else {
    classes.push("border hover:border-gray-300");
  }

  return classes;
});

const getDropdownClasses = computed(() => {
  const classes = [
    // Only apply these position classes when teleport is disabled
    !teleportOptions.value?.enabled && [
      "absolute",
      positionState.value.isFlippedVertical || props.dropUp
        ? "bottom-full"
        : props.filterIcon
          ? "top-3"
          : "top-full",
      {
        "left-0": !positionState.value.isFlippedHorizontal,
        "right-0": positionState.value.isFlippedHorizontal,
      },
    ],
    // Use fixed positioning for teleported dropdowns
    teleportOptions.value?.enabled && "fixed",
    props.fullWidth && "w-fit",
    !props.fullWidth && props.menuWidth,
    { "w-30": props.filterIcon },
    props.customZIndex ?? "z-50",
    {
      "overflow-auto":
        !!positionState.value.maxHeight || !!positionState.value.maxWidth,
    },
  ];

  return classes;
});

const getOptionClasses = (option: string) => {
  return [
    { capitalize: props.capitalize },
    isOptionDisabled(option) ? "text-gray-300" : "cursor-pointer",
  ];
};

const shouldShowLabel = computed(() => {
  return (
    props.showLabel &&
    (inputValue.value || Number.isInteger(inputValue.value)) &&
    !props.hidePlaceholderWhenValue
  );
});

const teleportOptions = computed(() => ({
  ...defaultTeleportOptions,
  ...props.teleportOptions,
}));

const showMenu = ref(false);
const dropdownTrigger = ref<HTMLElement | null>(null);
const dropdown = ref<HTMLElement | null>(null);
const search = ref("");
const minWidth = ref<null | string>(null);
const cachedOptions = ref<typeof props.options>({ ...props.options });
const dropdownTriggerVisibile = useElementVisibility(dropdownTrigger);

// Use both teleport and overflow detection for compatibility
const {
  top: teleportTop,
  left: teleportLeft,
  assignPositionValues,
} = useTeleport(
  dropdownTrigger,
  teleportOptions.value?.scrollable,
  "menu",
  null,
  teleportOptions.value?.horizontalScrollable
);

// Use overflow detection for smart positioning
const { positionState, setupEventListeners, updatePosition } =
  useOverflowDetection(dropdown, dropdownTrigger, showMenu, {
    scrollableContainerId: teleportOptions.value?.scrollable,
    offset: { top: 10, bottom: 10 },
    checkViewportOverflow: true,
    enableAutoSizing: true,
  });

const {
  value: inputValue,
  errorMessage,
  handleChange,
} = useField(props.name, undefined, {
  initialValue: props.value,
});

const dropdownStyle = computed(() => {
  const baseStyle: {
    minWidth?: string;
    maxHeight?: string;
    maxWidth?: string;
    overflowY?: "auto";
    overflowX?: "auto";
    width?: string;
  } = {
    minWidth: props.menuWidth ? undefined : (minWidth.value ?? "200px"),
    maxHeight: positionState.value.maxHeight
      ? `${positionState.value.maxHeight}px`
      : undefined,
    // Only apply maxWidth if fullWidth is false
    maxWidth:
      !props.fullWidth && positionState.value.maxWidth
        ? `${positionState.value.maxWidth}px`
        : undefined,
    overflowY: positionState.value.maxHeight ? ("auto" as const) : undefined,
    overflowX: positionState.value.maxWidth ? ("auto" as const) : undefined,
  };

  if (props.fullWidth) {
    baseStyle.width = "fit-content";
  } else if (props.menuWidth) {
    // Use menuWidth prop if provided
    baseStyle.width = props.menuWidth;
  } else if (dropdownTrigger.value) {
    // If not fullWidth but we have a trigger, use its width as a minimum
    baseStyle.width = `${Math.max(dropdownTrigger.value?.offsetWidth, 200)}px`;
  }

  if (!teleportOptions.value?.enabled) {
    return baseStyle;
  }

  // For backward compatibility, use both approaches
  // This ensures existing components continue to work while also benefiting from overflow detection
  return {
    ...baseStyle,
    top: positionState.value.isFlippedVertical
      ? `${teleportTop.value - (dropdown.value?.offsetHeight || 0) - 10}px`
      : `${teleportTop.value + props.dropdownOffsetTop}px`,
    left: positionState.value.isFlippedHorizontal
      ? `${teleportLeft.value - (dropdown.value?.offsetWidth || 0) + (dropdownTrigger.value?.offsetWidth || 0)}px`
      : `${teleportLeft.value}px`,
  };
});

const pickedValue = computed(() => {
  const valueFromOptions =
    props.options[`${inputValue.value}`] ||
    cachedOptions.value[`${inputValue.value}`];

  if (valueFromOptions) {
    return props.optionFormatter?.(valueFromOptions) || valueFromOptions;
  }

  return props.value || inputValue.value;
});

const sortedOptions = computed(() => {
  const options = props.sort ? sortProperties(props.options) : props.options;
  const searchValue = search.value.trim();

  if (!searchValue) {
    return options;
  }

  return Object.fromEntries(
    Object.entries(options).filter(([, option]: [string, string]) =>
      option.toLowerCase().includes(searchValue.toLowerCase())
    )
  );
});

const headlineTextClassname = computed(() => {
  if (props.customButtonClassnames?.headlineTextClassname) {
    return props.customButtonClassnames.headlineTextClassname;
  }

  const hasNoValue =
    inputValue.value !== 0 && inputValue.value !== false && !inputValue.value;

  if (props.disabled || hasNoValue) {
    return "text-gray-400";
  }

  return "text-headline";
});

const dropdownButtonWidth = computed(
  () => props.customButtonClassnames?.widthClassname ?? "w-full"
);

const dropdownButtonHeight = computed(
  () => props.customButtonClassnames?.heightClassname ?? "h-10"
);

const optionIcon = (id: string | number | boolean) => {
  return props.icons?.[String(id).trim()] || {};
};

const selectOption = (option: string | number) => {
  const optionValue = props.options[option];
  const isDisabled =
    props.disabledOptions?.length && isOptionDisabled(optionValue || "");

  if (isDisabled) {
    return;
  }

  const normalizedOption =
    typeof option === "string" ? option.trimStart() : option;

  toggleMenu();

  if (!props.isStateless) {
    handleChange(normalizedOption);
  }

  emit("change", normalizedOption);
};

const toggleMenu = () => {
  if (props.disabled) {
    return;
  }
  handleMenu(!showMenu.value);
};

const handleMenu = (value: boolean) => {
  showMenu.value = value;
  emit("toggle", value);
  emit("menu:toggle", showMenu.value);
};

onClickOutside(dropdown, (event) => {
  if (!showMenu.value) {
    return;
  }

  event.stopPropagation();
  handleMenu(false);
});

const clearFilter = () => {
  handleChange("");
  emit("change", "");
  handleMenu(false);
};

const isSelected = (val: string | number) => {
  const normalizedVal = typeof val === "string" ? val.trimStart() : val;
  return normalizedVal == inputValue.value;
};

const handleSearchChange = (value: string) => {
  search.value = value;
  emit("search:change", value);
};

const isOptionDisabled = (option: string): boolean =>
  !!props.disabledOptions?.map(String).includes(option);

// Watchers
watch(
  () => props.value,
  (updatedValue) => {
    handleChange(updatedValue);
  }
);

watch(
  () => props.active,
  (updatedActive) => {
    handleMenu(updatedActive);
  }
);

watch(
  () => props.options,
  () => {
    minWidth.value = getDropdownWidth(Object.values(props.options));

    // Trigger overflow detection when options change (for async loading)
    if (showMenu.value) {
      nextTick(() => {
        updatePosition();
      });
    }
  },
  { immediate: true }
);

watch(
  () => props.options,
  (newOptions) => {
    cachedOptions.value = {
      ...cachedOptions.value,
      ...newOptions,
    };
  }
);

// Animation frame management
let animationFrameId: number | null = null;

// Function to update teleport position with animation frame for smoother updates
const updateTeleportPosition = () => {
  if (!showMenu.value || !teleportOptions.value?.enabled) {
    return;
  }

  // Call the teleport's position update function
  assignPositionValues();

  // Continue the animation loop if menu is still open
  if (showMenu.value) {
    animationFrameId = requestAnimationFrame(updateTeleportPosition);
  }
};

watch(dropdownTriggerVisibile, (isTriggerVisible) => {
  if ((showMenu.value || props.toggleFromParent) && !isTriggerVisible) {
    handleMenu(false);
  }
});

// Setup and cleanup
onMounted(() => {
  // Close dropdown on window resize for better UX
  window.addEventListener("resize", () => handleMenu(false));

  // Set up event listeners from the overflow detection composable
  const cleanup = setupEventListeners();

  // Watch for menu visibility changes
  watch(showMenu, (isVisible) => {
    if (!isVisible) {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
      return;
    }

    if (!teleportOptions.value?.enabled) {
      return;
    }

    // Initial position update
    nextTick(() => {
      assignPositionValues();

      // Update width for fullWidth dropdowns when teleported
      if (props.fullWidth && dropdown.value && dropdownTrigger.value) {
        dropdown.value.style.width = `${dropdownTrigger.value.offsetWidth}px`;
      }

      // Start continuous updates
      if (!animationFrameId) {
        animationFrameId = requestAnimationFrame(updateTeleportPosition);
      }
    });
  });

  onBeforeUnmount(() => {
    // Clean up animation frame
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
    }

    cleanup();

    window.removeEventListener("resize", () => handleMenu(false));
  });
});

defineExpose({ search, showMenu: readonly(showMenu), toggleMenu });
</script>

<style>
@reference "@/assets/tailwind.css";

.document-upload-dd ul {
  @apply w-auto;
}

.document-upload-dd li {
  @apply min-w-max;
}
</style>
